import React, { useState, useEffect, useMemo, ReactNode } from 'react';
import { Grid, Box } from '@mui/material';
import { FlexyTextField, ItemActions, useL2DTheme } from '@europrocurement/flexy-components';
import { useDebouncedCallback } from 'use-debounce';
import { PreFilterDatatable, SearchOpts, PreFilter } from '../DatatableHeader';
import { FlexyFiltersList } from '../FlexyFiltersList';
import { FlexyPreFilters } from '../FlexyPreFilters';

export type FlexyFiltersProps = {
    searchOpts?: SearchOpts;
    buttonAction?: ReactNode;
    onRefresh?: () => void;
    debounce?: number;
};

const isPreFilterActive = (
    preFilterItem: PreFilterDatatable,
    activeCriterion: Record<string, unknown>,
    mustHaveAFilter: boolean,
): boolean => {
    if (mustHaveAFilter && preFilterItem.filters.length === 0) {
        return false;
    }
    const totalCount = preFilterItem.filters.reduce((currentCount: number, f: PreFilter) => {
        if (activeCriterion[f.field] === f.value) {
            return currentCount + 1;
        }

        if (Array.isArray(f.value) && Array.isArray(activeCriterion[f.field])) {
            const tmp = (activeCriterion[f.field] as unknown[]).filter((o) => f.value === o);

            if (tmp.length === 0) {
                return currentCount + 1;
            }
        }

        return currentCount;
    }, 0);

    return totalCount === preFilterItem.filters.length;
};

export const FlexyFilters: React.FunctionComponent<FlexyFiltersProps> = function ({
    searchOpts,
    buttonAction,
    onRefresh,
    debounce = 500,
}: FlexyFiltersProps) {
    const { palette } = useL2DTheme();
    const [currentPreFilter, setCurrentPreFilter] = useState<PreFilterDatatable>();
    const [expandedFilters, setExpandedFilters] = useState<boolean>(false);
    const preFilters = useMemo(
        () => searchOpts?.filtersOpts?.preFilters ?? { items: [] },
        [searchOpts?.filtersOpts?.preFilters],
    );
    const activePreFilters = searchOpts?.filtersOpts?.activePreFilter;

    const hasHiddenFilters = useMemo(
        () => searchOpts?.filtersOpts?.filters.some((f) => f.hidden) ?? false,
        [searchOpts?.filtersOpts?.filters],
    );

    const hasActiveFilters = useMemo(() => {
        const activeFilters = searchOpts?.filtersOpts?.activeFilters || {};
        const filtersKeys = (searchOpts?.filtersOpts?.filters || []).map((f) => f.field);
        return Object.keys(activeFilters).some((k) => filtersKeys.includes(k));
    }, [searchOpts?.filtersOpts?.activeFilters, searchOpts?.filtersOpts?.filters]);

    const [innerValue, setInnerValue] = React.useState<string>(searchOpts?.search || '');

    useEffect(() => {
        let active = preFilters.items.find((item) =>
            isPreFilterActive(item, activePreFilters || {}, true),
        );

        if (!active) {
            active = preFilters.items.find((item) =>
                isPreFilterActive(item, activePreFilters || {}, false),
            );
        }

        if (active) {
            setCurrentPreFilter(active);
        }
    }, [preFilters, activePreFilters]);

    const applyPreFilter = (preFilter: PreFilterDatatable) => {
        setCurrentPreFilter(preFilter);
        searchOpts?.filtersOpts?.onUpdateFilters(preFilter);
    };

    const onChangeDebounce = useDebouncedCallback((value: string) => {
        if (searchOpts?.onSearchChange) {
            searchOpts?.onSearchChange(value);
        }
    }, debounce);

    return (
        <Grid
            container
            direction="column"
            alignItems="end"
        >
            <Grid
                item
                sx={{ width: '100%' }}
            >
                <Box
                    display="flex"
                    flexDirection="row-reverse"
                    justifyContent="space-between"
                    alignItems="end"
                    gap={2}
                >
                    <Box>{buttonAction}</Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'start',
                            justifyContent: 'start',
                            width: '100%',
                        }}
                    >
                        {preFilters.items.length > 0 && (
                            <FlexyPreFilters
                                filters={preFilters}
                                onClick={applyPreFilter}
                                current={currentPreFilter}
                            />
                        )}
                        {searchOpts?.showSearch ? (
                            <FlexyTextField
                                sx={{
                                    marginTop: '15px',
                                }}
                                inputProps={{
                                    style: {
                                        padding: '14px 14px',
                                    },
                                }}
                                value={innerValue}
                                onChange={(e) => {
                                    setInnerValue(e.target.value);
                                    onChangeDebounce(e.target.value);
                                }}
                                variant="outlined"
                                fullWidth
                                placeholder="Recherche textuelle"
                            />
                        ) : null}
                    </Box>
                </Box>
            </Grid>
            <Grid
                item
                alignSelf="start"
                sx={{ marginTop: '15px', width: '100%' }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            flex: '1 1 auto',
                            minWidth: 0,
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            '&::-webkit-scrollbar': {
                                height: '6px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'grey.300',
                                borderRadius: '3px',
                            },
                            scrollbarColor: `${palette.grey[300]} transparent`,
                        }}
                    >
                        <FlexyFiltersList
                            filtersOpts={searchOpts?.filtersOpts}
                            showHiddenFilters={expandedFilters}
                        />
                    </Box>
                    <Box
                        sx={{
                            position: 'relative',
                            flexShrink: 0,
                            bgcolor: palette.background.default,
                            ml: '-5px',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                right: '100%',
                                top: 0,
                                bottom: 0,
                                width: '40px',
                                background: `linear-gradient(to right, transparent, ${palette.background.default})`,
                                pointerEvents: 'none',
                            }}
                        />
                        <ItemActions
                            onExpand={
                                hasHiddenFilters
                                    ? () => setExpandedFilters(!expandedFilters)
                                    : undefined
                            }
                            expanded={expandedFilters}
                            expandTooltip={
                                expandedFilters ? 'Masquer les filtres' : 'Afficher les filtres'
                            }
                            onDelete={
                                hasActiveFilters
                                    ? () => searchOpts?.filtersOpts?.onFiltersClear()
                                    : undefined
                            }
                            deleteTooltip="Effacer les filtres"
                            onRefresh={onRefresh}
                            refreshTooltip="Rafraîchir les données"
                            size={28}
                            iconSize="13px"
                            sx={{ marginBottom: '14px' }}
                        />
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
};
